import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Save, Send, Plus, Minus, Search, FileText, AlertCircle, Copy, XCircle, GripVertical, Download, RotateCcw, ChevronDown, ChevronUp, X } from 'lucide-react';
import { flyersService } from '../../services/flyersService';
import { PROMO_FILTRY, PromoFiltr, sedneNaFiltr } from '../../utils/promoSkupiny';
import { productsService } from '../../services/productsService';
import { promoImagesService } from '../../services/promoImagesService';
import { categoriesService } from '../../services/categoriesService';
import { brandsService } from '../../services/brandsService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FlyerPageView } from '../../components/flyer/FlyerPageView';
import { DraggableProduct } from '../../components/flyer/DraggableProduct';
import { DraggablePromoImage } from '../../components/flyer/DraggablePromoImage';
import { RejectionHistory } from '../../components/flyer/RejectionHistory';
import { ValidationErrorsModal } from '../../components/flyer/ValidationErrorsModal';
import { Product, FlyerPage, FlyerSlot } from '../../types';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useAuthStore } from '../../store/authStore';
import { sanitizePdfFilename } from '../../utils/helpers';
import { jeUzavrenyArchivProdejce } from '../../utils/letakZalozky';

// Sortable Page Button Component
const SortablePageButton: React.FC<{
  id: string;
  index: number;
  isActive: boolean;
  isFirst: boolean;
  isLocked: boolean;
  onClick: () => void;
}> = ({ id, index, isActive, isFirst, isLocked, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isFirst || isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center">
      {!isFirst && !isLocked && (
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mr-1">
          <GripVertical className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </div>
      )}
      <button
        onClick={onClick}
        className={`w-10 h-10 rounded ${
          isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'
        } ${isFirst ? 'ml-5' : ''}`}
      >
        {index + 1}
      </button>
    </div>
  );
};

const KONTROLA_KOPIE_KLIC = 'eflyer.kontrolaKopie.';

/** Hodnota filtru podkategorie pro produkty bez podkategorie (id jsou UUID, nekoliduje). */
const BEZ_PODKATEGORIE = '__bez__';

/**
 * První řádek 1. strany (sloty 0 a 1) patří hlavičce. Je navíc nižší (pod mřížkou je
 * patička), takže produkt by se do něj nevešel – ikony by přetekly přes ceny.
 */
const jeRadekHlavicky = (pageNumber: number, slotIndex: number) => pageNumber === 1 && slotIndex < 2;
const HLASKA_RADEK_HLAVICKY = 'Do prvního řádku první stránky nelze vložit produkt – patří tam hlavička.';

const nevyrizenaKontrolaKopie = (flyerId?: string): boolean => {
  if (!flyerId || flyerId === 'new') return false;
  try {
    return localStorage.getItem(KONTROLA_KOPIE_KLIC + flyerId) === '1';
  } catch {
    return false;
  }
};

const nastavKontroluKopie = (flyerId: string, nevyrizena: boolean) => {
  try {
    if (nevyrizena) localStorage.setItem(KONTROLA_KOPIE_KLIC + flyerId, '1');
    else localStorage.removeItem(KONTROLA_KOPIE_KLIC + flyerId);
  } catch {
    // Bez localStorage hláška vydrží jen do obnovení stránky.
  }
};

export const FlyerEditorPage: React.FC = () => {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Determine if this is a new flyer based on URL
  const isNewFromUrl = location.pathname.endsWith('/new');
  const id = isNewFromUrl ? 'new' : params.id;
  const isNew = id === 'new';

  // Debug: Log component mount and URL changes
  console.log('🔍 FlyerEditorPage render - params:', params, 'id:', id, 'location:', location.pathname, 'isNewFromUrl:', isNewFromUrl);

  // Determine base path based on current location
  const isMyFlyers = location.pathname.startsWith('/my-flyers');
  const basePath = isMyFlyers ? '/my-flyers' : '/flyers';

  // Povinná hláška po vytvoření kopie letáku (ne pro Prodejce): drží se, dokud ji uživatel
  // neodklikne - i přes obnovení stránky (příznak v localStorage podle id kopie).
  const [kopieKontrola, setKopieKontrola] = useState(() => nevyrizenaKontrolaKopie(id));
  useEffect(() => {
    setKopieKontrola(nevyrizenaKontrolaKopie(id));
  }, [id]);
  const potvrditKontroluKopie = () => {
    if (id) nastavKontroluKopie(id, false);
    setKopieKontrola(false);
  };

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [search, setSearch] = useState('');

  // Rozšířené filtry produktů - jen pro prodejce (jeho leták se skládá
  // z produktů aktivních letáků, kterých bývá hodně a nejde je stránkovat hledáním).
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterEan, setFilterEan] = useState('');
  const [filterBrandId, setFilterBrandId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterSubcategoryId, setFilterSubcategoryId] = useState('');
  const [filterInstallationType, setFilterInstallationType] = useState<'' | 'BUILT_IN' | 'FREESTANDING'>('');
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [priceRange, setPriceRange] = useState<[number, number] | null>(null);
  const [promoSearch, setPromoSearch] = useState('');
  // Filtr promo obrázků podle použití - přepínač, vždy platí právě jedna volba.
  const [promoFiltr, setPromoFiltr] = useState<PromoFiltr>('vse');
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const previousSearchRef = useRef(search);
  const [activeTab, setActiveTab] = useState<'products' | 'promos'>('products');
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Sensors for drag and drop - use PointerSensor with distance to prevent conflicts
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  const preparePagesForAPI = (pages: FlyerPage[]): any[] => {
    return pages.map(page => ({
      pageNumber: page.pageNumber,
      footerPromoImageId: page.footerPromoImageId || null,
      slots: page.slots.map(slot => ({
        type: slot?.type || 'empty',
        productId: slot?.type === 'product' && slot.product ? slot.product.id : null,
        promoImageId: slot?.type === 'promo' && slot.promoImage ? slot.promoImage.id : null,
        promoSize: slot?.promoSize || null,
      })),
    }));
  };

  const [flyerData, setFlyerData] = useState<{
    name: string;
    actionId?: number;
    actionName?: string;
    validFrom: string;
    validTo: string;
    pages: FlyerPage[];
  }>({
    name: 'Nový leták',
    actionId: undefined,
    actionName: undefined,
    validFrom: '',
    validTo: '',
    pages: [{
      id: '1',
      pageNumber: 1,
      slots: Array(8).fill({ type: 'empty' } as FlyerSlot),
    }] as FlyerPage[],
  });

  const { data: flyer, isLoading } = useQuery({
    queryKey: ['flyers', id],
    queryFn: () => flyersService.getFlyer(id!),
    enabled: !isNew,
  });

  // Check if flyer is locked for editing
  // For end users on /my-flyers: only lock if expired
  // For suppliers: lock if active, pending_approval, or expired
  const isLocked = isMyFlyers && user?.role === 'end_user'
    ? flyer?.status === 'expired'
    : (flyer?.status === 'active' || flyer?.status === 'pending_approval' || flyer?.status === 'expired');

  // Prodejce platnost nezadává - obě data určuje server: „od" je den založení
  // letáku, „do" se dopočítá z letáků, ze kterých produkty pochází. Pole proto
  // jen ukazují, co vyšlo. Backend hodnotu z formuláře stejně ignoruje.
  const jeProdejce = user?.role === 'end_user';
  // Dodavateli platnost určuje vybraná prodejní akce z ERP (rozhodnuto 5. 10. 2026),
  // pole jsou jen k nahlédnutí. Backend data z formuláře u dodavatele ignoruje.
  const jeDodavatel = user?.role === 'supplier';

  // Vlastní archivovaný leták prodejce po uplynutí platnosti: jen k nahlédnutí.
  // Žádné Obnovit, žádné PDF - viz jeUzavrenyArchivProdejce. Kopii nenabízíme
  // u archivovaného letáku vůbec, i kdyby platnost ještě běžela.
  const jeVlastniArchiv = jeProdejce && flyer?.supplierId === user?.id && flyer?.status === 'expired';
  const jeUzavrenyArchiv =
    jeVlastniArchiv && jeUzavrenyArchivProdejce(flyer as any, jeProdejce);

  // For end users, get products from active flyers instead of all products
  const { data: activeFlyers = [] } = useQuery({
    queryKey: ['flyers', 'active'],
    queryFn: () => flyersService.getActiveFlyers(),
    enabled: isMyFlyers, // Only fetch for end users
  });

  // For suppliers, fetch products normally
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', 'flyer-editor', search, productPage],
    queryFn: () => productsService.getProducts({
      search: search || undefined,
      page: productPage,
      limit: 20,
      isActive: true,
    }),
    enabled: !isMyFlyers, // Only fetch for suppliers
  });

  // Při změně stránky nebo při prvním načtení přidáme produkty do seznamu
  useEffect(() => {
    if (isMyFlyers) {
      // For end users: extract products from active flyers
      if (activeFlyers.length > 0) {
        const productsFromActiveFlyers = activeFlyers.flatMap(flyer =>
          flyer.pages.flatMap(page =>
            page.slots
              .filter(slot => slot && slot.type === 'product' && slot.product)
              .map(slot => slot.product!)
          )
        );

        // Remove duplicates by product ID
        const uniqueProducts = Array.from(
          new Map(productsFromActiveFlyers.map(p => [p.id, p])).values()
        );

        // Apply search filter
        const filtered = search
          ? uniqueProducts.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
          : uniqueProducts;

        // Only update if different
        setAllProducts(prev => {
          if (JSON.stringify(prev.map(p => p.id)) === JSON.stringify(filtered.map(p => p.id))) {
            return prev;
          }
          return filtered;
        });
      } else {
        setAllProducts([]);
      }
    } else {
      // For suppliers: use normal product pagination
      if (productsData?.data) {
        // Pokud se změnil search, resetuj seznam
        if (previousSearchRef.current !== search) {
          previousSearchRef.current = search;
          setProductPage(1);
          setAllProducts(productsData.data);
        } else {
          // Normální paginace - přidej nebo nahraď
          if (productPage === 1) {
            setAllProducts(productsData.data);
          } else {
            setAllProducts(prev => [...prev, ...productsData.data]);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsData, productPage, search, isMyFlyers, activeFlyers.length]);

  const { data: promoImages = [] } = useQuery({
    queryKey: ['promo-images', user?.role],
    queryFn: () => promoImagesService.getPromoImages(),
  });

  // Číselníky pro rozšířené filtry (načítají se jen tam, kde se filtry zobrazují)
  const { data: filterCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesService.getAllCategories(),
    enabled: isMyFlyers,
  });

  const { data: filterBrands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: () => brandsService.getAllBrands(), // stejný fetcher jako ostatní stránky s klíčem [brands]
    enabled: isMyFlyers,
  });

  const { data: filterSubcategories = [] } = useQuery({
    queryKey: ['subcategories', filterCategoryId],
    queryFn: () => categoriesService.getSubcategories(filterCategoryId),
    enabled: isMyFlyers && !!filterCategoryId,
  });

  // Prodejní akce z ERP pro dropdown výběru akce na letáku.
  const { data: actions = [] } = useQuery({
    queryKey: ['flyers', 'actions'],
    queryFn: () => flyersService.getActions(),
  });

  useEffect(() => {
    if (flyer) {
      setFlyerData({
        name: flyer.name,
        actionId: flyer.actionId,
        actionName: flyer.actionName,
        validFrom: flyer.validFrom ? new Date(flyer.validFrom).toISOString().split('T')[0] : '',
        validTo: flyer.validTo ? new Date(flyer.validTo).toISOString().split('T')[0] : '',
        pages: flyer.pages.length > 0 ? flyer.pages : [{
          id: '1',
          pageNumber: 1,
          slots: Array(8).fill({ type: 'empty' } as FlyerSlot),
        }],
      });
    }
  }, [flyer]);

  // Nový leták prodejce má „platnost od" = dnešek. Tohle je jen předvyplnění
  // formuláře, ať uživatel hned vidí, co se uloží; rozhodující je stejné datum,
  // které si backend nastaví sám při založení letáku.
  useEffect(() => {
    if (jeProdejce && isNew) {
      setFlyerData((d) =>
        d.validFrom ? d : { ...d, validFrom: new Date().toISOString().split('T')[0] },
      );
    }
  }, [jeProdejce, isNew]);

  const saveDraftMutation = useMutation({
    mutationFn: async (data: typeof flyerData) => {
      const wasNew = id === 'new';
      console.log('🔍 saveDraftMutation - id:', id, 'wasNew:', wasNew);

      if (wasNew) {
        console.log('🔍 Creating new flyer...');
        const created = await flyersService.createFlyer({
          name: data.name,
          actionId: data.actionId,
          actionName: data.actionName,
          validFrom: data.validFrom,
          validTo: data.validTo,
        });
        console.log('🔍 Flyer created with ID:', created.id);

        const updated = await flyersService.updateFlyer(created.id, {
          name: data.name,
          actionId: data.actionId,
          actionName: data.actionName,
          validFrom: data.validFrom,
          validTo: data.validTo,
          pages: preparePagesForAPI(data.pages)
        });
        console.log('🔍 Flyer updated, returning with wasNew flag');
        return { ...updated, wasNew: true };
      }

      if (!id) {
        throw new Error('Flyer ID is missing');
      }

      console.log('🔍 Updating existing flyer with ID:', id);
      return flyersService.updateFlyer(id, {
        name: data.name,
        actionId: data.actionId,
        actionName: data.actionName,
        validFrom: data.validFrom,
        validTo: data.validTo,
        pages: preparePagesForAPI(data.pages),
      });
    },
    onSuccess: (data) => {
      console.log('🔍 saveDraftMutation success, data:', data);
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      if ((data as any).wasNew) {
        console.log('🔍 Navigating to:', `${basePath}/${data.id}`);
        navigate(`${basePath}/${data.id}`, { replace: true });
      }
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        const created = await flyersService.createFlyer({
          name: flyerData.name,
          actionId: flyerData.actionId,
          actionName: flyerData.actionName,
          validFrom: flyerData.validFrom,
          validTo: flyerData.validTo,
        });
        await flyersService.updateFlyer(created.id, {
          pages: preparePagesForAPI(flyerData.pages)
        });
        await flyersService.submitForApproval(created.id);
        return created;
      }
      await flyersService.updateFlyer(id!, {
        name: flyerData.name,
        actionId: flyerData.actionId,
        actionName: flyerData.actionName,
        validFrom: flyerData.validFrom,
        validTo: flyerData.validTo,
      });
      return flyersService.submitForApproval(id!);
    },
    onSuccess: () => {
      // Invalidate both detail and list queries to refresh rejection status
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      queryClient.invalidateQueries({ queryKey: ['flyers', 'my'] });
      navigate(basePath);
    },
  });

  const { isSaving, lastSaved } = useAutoSave({
    data: flyerData,
    onSave: async (data) => {
      if (!isNew) await saveDraftMutation.mutateAsync(data);
    },
    delay: 120000, // 2 minutes
    enabled: !isNew,
  });

  // Získá PDF blob aktuálního letáku. Pro draft / prodejce nejdřív uloží
  // stav a přegeneruje PDF (ať odpovídá tomu, co uživatel vidí). Vrací null, když
  // nelze pokračovat (nový/neuložený leták nebo chybí patička) - důvod už zobrazil.
  const preparePdfBlob = async (): Promise<Blob | null> => {
    if (isNew || !id) {
      alert('Prosím nejdříve uložte leták');
      return null;
    }

    // (Re)generujeme PDF jen z KONCEPTU (odráží aktuální úpravy). U nedraftu
    // (archivovaný/expired, aktivní, schválený) se jen zobrazí uložené PDF — regenerace
    // by u nedraftu volala uložení (updateFlyer), které jde jen na draft → chyba.
    const willGenerate = flyer?.status === 'draft';

    // When generating, page 1 must have a footer (patička) and a header (hlavička) -
    // warn immediately, no server round-trip.
    if (willGenerate && !flyerData.pages[0]?.footerPromoImageId) {
      alert('Leták nemá patičku. Přidejte na první stránku patičku (promo velikosti „Patička") před vygenerováním PDF.');
      return null;
    }
    const headerSlot = flyerData.pages[0]?.slots?.[0];
    const hasHeader =
      headerSlot?.type === 'promo' &&
      (headerSlot.promoSize === 'header_2x1' || headerSlot.promoSize === 'header_2x2') &&
      // Osiřelý slot bez obrázku (smazané promo hlavičky) hlavička není.
      !!headerSlot.promoImage;
    if (willGenerate && !hasHeader) {
      alert('Leták nemá hlavičku. Přidejte na první stránku hlavičku (promo velikosti „Hlavička 2×1" nebo „Hlavička 2×2") před vygenerováním PDF.');
      return null;
    }

    if (willGenerate) {
      // Save the current editor state first, so the PDF reflects exactly what the user sees
      // (never a stale saved version) and the just-added footer is persisted before generation.
      await saveDraftMutation.mutateAsync(flyerData);
      await flyersService.generatePdf(id);
      return await flyersService.getPdfBlob(id, true);
    }
    // For non-drafts (pending_approval, approved, active), only show saved PDF
    return await flyersService.getPdfBlob(id, false);
  };

  const handleViewPdf = async () => {
    try {
      setIsGeneratingPdf(true);
      const pdfBlob = await preparePdfBlob();
      if (!pdfBlob) return;

      const blobUrl = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(blobUrl, `pdf_${id}_${Date.now()}`);
      if (!newWindow) {
        alert('Povolte vyskakovací okna');
        URL.revokeObjectURL(blobUrl);
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (error: any) {
      // Show the specific backend message (e.g. missing footer) when available
      alert(error?.response?.data?.message || 'Chyba při zobrazení PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsGeneratingPdf(true);
      const pdfBlob = await preparePdfBlob();
      if (!pdfBlob) return;

      // Stáhnout pod čitelným názvem letáku (blob sám jméno nenese -> a.download).
      const blobUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = sanitizePdfFilename(flyer?.name, id);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Chyba při stažení PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const product = allProducts.find(p => p.id === event.active.id);
    if (product) setActiveProduct(product);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProduct(null);
    if (!over || isLocked) return; // Prevent drag & drop when locked

    const activeId = active.id as string;
    const overId = over.id as string;

    // Check if this is a page reordering event
    if (activeId.startsWith('page-button-') && overId.startsWith('page-button-')) {
      const oldIndex = parseInt(activeId.replace('page-button-', ''));
      const newIndex = parseInt(overId.replace('page-button-', ''));

      // Don't allow moving to/from first position (page 0)
      if (oldIndex === 0 || newIndex === 0 || oldIndex === newIndex) return;

      const newPages = arrayMove(flyerData.pages, oldIndex, newIndex);
      // Update page numbers to match new order
      const updatedPages = newPages.map((page, index) => ({
        ...page,
        pageNumber: index + 1,
      }));

      setFlyerData({ ...flyerData, pages: updatedPages });

      // Update current page index if needed
      if (currentPageIndex === oldIndex) {
        setCurrentPageIndex(newIndex);
      } else if (currentPageIndex === newIndex) {
        setCurrentPageIndex(oldIndex < newIndex ? currentPageIndex - 1 : currentPageIndex + 1);
      } else if (oldIndex < currentPageIndex && newIndex >= currentPageIndex) {
        setCurrentPageIndex(currentPageIndex - 1);
      } else if (oldIndex > currentPageIndex && newIndex <= currentPageIndex) {
        setCurrentPageIndex(currentPageIndex + 1);
      }

      return;
    }

    const dropId = overId;

    // Přesun / výměna v rámci letáku: táhnutí obsazeného jednoslotového slotu na jiný slot.
    if (activeId.startsWith('move-page-')) {
      const srcMatch = activeId.match(/move-page-(\d+)-slot-(\d+)/);
      const tgtMatch = dropId.match(/page-(\d+)-slot-(\d+)/);
      if (!srcMatch || !tgtMatch) return;
      const srcPage = parseInt(srcMatch[1]);
      const srcSlot = parseInt(srcMatch[2]);
      const tgtPage = parseInt(tgtMatch[1]);
      const tgtSlot = parseInt(tgtMatch[2]);
      if (srcPage !== tgtPage || srcSlot === tgtSlot) return;

      const newPages = [...flyerData.pages];
      const slots = [...newPages[srcPage].slots];
      const source = slots[srcSlot];
      const target = slots[tgtSlot];

      // Cíl musí být stejné velikosti = jednoslotový (prázdný / produkt / 1×1 promo).
      const isSingle = (s?: FlyerSlot) =>
        !s || s.type === 'empty' || s.type === 'product' || (s.type === 'promo' && (!s.promoSize || s.promoSize === 'single'));
      if (!isSingle(target)) return; // víceslotová promo (hlavička/2×2/…) na cíli → nedovolit

      // Produkt do řádku hlavičky nesmí ani přesunem; při výměně se tam nesmí dostat
      // ani produkt z druhé strany.
      const pageNumber = newPages[srcPage].pageNumber;
      if (
        (source?.type === 'product' && jeRadekHlavicky(pageNumber, tgtSlot)) ||
        (target?.type === 'product' && jeRadekHlavicky(pageNumber, srcSlot))
      ) {
        alert(HLASKA_RADEK_HLAVICKY);
        return;
      }

      // obsazeno → výměna, prázdno → přesun
      slots[srcSlot] = target && target.type !== 'empty' ? target : { type: 'empty' };
      slots[tgtSlot] = source;
      newPages[srcPage] = { ...newPages[srcPage], slots };
      setFlyerData({ ...flyerData, pages: newPages });
      return;
    }

    // Footer drop
    const footerMatch = dropId.match(/page-(\d+)-footer/);
    if (footerMatch) {
      const pageIndex = parseInt(footerMatch[1]);
      const promoId = active.id.toString().startsWith('promo-') ? active.id.toString().replace('promo-', '') : null;
      const promo = promoId ? promoImages.find(p => p.id === promoId) : null;

      if (promo && promo.defaultSize === 'footer') {
        const newPages = [...flyerData.pages];
        newPages[pageIndex] = { ...newPages[pageIndex], footerPromoImage: promo, footerPromoImageId: promo.id };
        setFlyerData({ ...flyerData, pages: newPages });
      } else if (promo) {
        alert('Do patičky pouze promo s velikostí "Patička"');
      }
      return;
    }

    // Slot drop
    const slotMatch = dropId.match(/page-(\d+)-slot-(\d+)/);
    if (!slotMatch) return;

    const pageIndex = parseInt(slotMatch[1]);
    const slotIndex = parseInt(slotMatch[2]);

    const product = allProducts.find(p => p.id === active.id);
    const promoId = active.id.toString().startsWith('promo-') ? active.id.toString().replace('promo-', '') : null;
    const promo = promoId ? promoImages.find(p => p.id === promoId) : null;

    // Panel položku (kromě hlavičky, která si horní sloty řídí sama) lze pustit jen do PRÁZDNÉHO slotu.
    // Přesun/výměnu v rámci letáku řeší větev move-* výše.
    const isHeaderPromo = !!promo && (promo.defaultSize === 'header_2x1' || promo.defaultSize === 'header_2x2');
    // Celostránkové promo zabírá celou stranu (vyčistí všech 8 slotů), takže ho lze pustit
    // na kterýkoli slot – i obsazený. Hlavička si horní sloty taky řídí sama.
    const isFullPagePromo = !!promo && promo.defaultSize === 'full_page';
    const targetSlotState = flyerData.pages[pageIndex]?.slots[slotIndex];
    if (targetSlotState && targetSlotState.type !== 'empty' && !isHeaderPromo && !isFullPagePromo) return;

    const newPages = [...flyerData.pages];
    const newSlots = [...newPages[pageIndex].slots];

    if (product) {
      if (jeRadekHlavicky(newPages[pageIndex].pageNumber, slotIndex)) {
        alert(HLASKA_RADEK_HLAVICKY);
        return;
      }
      newSlots[slotIndex] = { type: 'product', product };
    } else if (promo) {
      if (promo.defaultSize === 'footer') {
        alert('Promo "Patička" pouze do patičky na stránce 1');
        return;
      }
      if (promo.defaultSize === 'full_page' && newPages[pageIndex].pageNumber === 1) {
        // Na první straně musí být hlavička, takže celostránkové promo sem nepatří.
        alert('Celostránkové promo nelze vložit na první stránku – ta musí mít hlavičku.');
        return;
      }
      if (promo.defaultSize === 'header_2x1' || promo.defaultSize === 'header_2x2') {
        // Hlavička jde jen na první stránku, vždy nahoře, a vyčistí sloty pod sebou.
        if (newPages[pageIndex].pageNumber !== 1) {
          alert('Hlavičku lze vložit pouze na první stránku');
          return;
        }
        const spanned = promo.defaultSize === 'header_2x1' ? [0, 1] : [0, 1, 2, 3];
        spanned.forEach((i) => { newSlots[i] = { type: 'empty' }; });
        newSlots[0] = { type: 'promo', promoImage: promo, promoSize: promo.defaultSize };
        newPages[pageIndex] = { ...newPages[pageIndex], slots: newSlots };
        setFlyerData({ ...flyerData, pages: newPages });
        return;
      }
      // Víceslotové promo (2×1, 2×2, celostránkové) překryje i sousední sloty.
      // Vyčistit je, aby pod promem nezůstal "neviditelný" produkt, který se
      // v datech pořád tváří jako použitý a blokuje své nasazení jinde.
      const size = promo.defaultSize;
      const col = slotIndex % 2;
      const row = Math.floor(slotIndex / 2);

      // Víceslotové promo musí zůstat celé v mřížce 2×4, jinak se vykreslí přes okraj
      // (2×2 v posledním řádku by přeteklo dolů přes patičku, cokoli v pravém sloupci vpravo).
      // Vkládá se proto vždy do levého slotu řádku a 2×2 ne do posledního (4.) řádku.
      if ((size === 'horizontal' || size === 'square') && col !== 0) {
        alert(
          size === 'square'
            ? 'Promo „2×2" vkládejte do levého slotu řádku – zabírá dva sloty na šířku.'
            : 'Promo „2×1" vkládejte do levého slotu řádku – zabírá dva sloty na šířku.',
        );
        return;
      }
      if (size === 'square' && row > 2) {
        alert('Promo „2×2" se do posledního řádku nevejde – zabírá dva řádky. Vložte ho nejvýše do 3. řádku.');
        return;
      }

      const idx = (r: number, c: number) => r * 2 + c;
      let spanned: number[] = [slotIndex];
      if (size === 'horizontal') {
        spanned = col + 1 < 2 ? [slotIndex, idx(row, col + 1)] : [slotIndex];
      } else if (size === 'square') {
        spanned = [];
        for (let r = row; r <= row + 1 && r < 4; r++) {
          for (let c = col; c <= col + 1 && c < 2; c++) {
            spanned.push(idx(r, c));
          }
        }
      } else if (size === 'full_page') {
        spanned = [0, 1, 2, 3, 4, 5, 6, 7];
      }
      spanned.forEach((i) => { newSlots[i] = { type: 'empty' }; });
      // Celostránkové promo kotvíme vždy do slotu 0 (levý horní roh), ať se v datech i v PDF
      // vykresluje přes celou stranu bez ohledu na to, kam ho uživatel pustil.
      const anchor = size === 'full_page' ? 0 : slotIndex;
      newSlots[anchor] = { type: 'promo', promoImage: promo, promoSize: size };
    } else return;

    newPages[pageIndex] = { ...newPages[pageIndex], slots: newSlots };
    setFlyerData({ ...flyerData, pages: newPages });
  };

  const handleRemoveFooter = () => {
    const newPages = [...flyerData.pages];
    newPages[currentPageIndex] = { ...newPages[currentPageIndex], footerPromoImage: null, footerPromoImageId: null };
    setFlyerData({ ...flyerData, pages: newPages });
  };

  const handleRemoveProduct = (slotIndex: number) => {
    const newPages = [...flyerData.pages];
    const newSlots = [...newPages[currentPageIndex].slots];
    newSlots[slotIndex] = { type: 'empty' };
    newPages[currentPageIndex] = { ...newPages[currentPageIndex], slots: newSlots };
    setFlyerData({ ...flyerData, pages: newPages });
  };

  const handleAddPage = () => {
    const newPage: FlyerPage = {
      id: Date.now().toString(),
      pageNumber: flyerData.pages.length + 1,
      slots: Array(8).fill({ type: 'empty' } as FlyerSlot),
    };
    setFlyerData({ ...flyerData, pages: [...flyerData.pages, newPage] });
    setCurrentPageIndex(flyerData.pages.length);
  };

  const handleRemovePage = (index: number) => {
    if (flyerData.pages.length === 1) {
      alert('Nelze smazat poslední stránku');
      return;
    }
    // Zakázat smazání první stránky (pageNumber === 1), protože je jiná (má footer)
    if (flyerData.pages[index].pageNumber === 1) {
      alert('Nelze smazat první stránku, protože má specifický layout s paticí');
      return;
    }
    const newPages = flyerData.pages.filter((_, i) => i !== index);
    setFlyerData({ ...flyerData, pages: newPages });
    if (currentPageIndex >= newPages.length) setCurrentPageIndex(newPages.length - 1);
  };

  const handleSubmit = async () => {
    if (!flyerData.actionId) {
      alert('Vyberte akci');
      return;
    }
    if (!flyerData.validFrom || !flyerData.validTo) {
      alert('Vybraná akce nemá v ERP vyplněnou platnost – leták nejde odeslat.');
      return;
    }

    // Validate flyer before submitting
    if (!id || id === 'new') {
      alert('Nejprve uložte leták');
      return;
    }

    // Stejné kontroly jako pro generování PDF: první stránka musí mít patičku i hlavičku.
    // Co brání vytvoření PDF, musí bránit i odeslání k autorizaci (backend to i tak vynutí).
    if (!flyerData.pages[0]?.footerPromoImageId) {
      alert('Leták nemá patičku. Přidejte na první stránku patičku (promo velikosti „Patička").');
      return;
    }
    const submitHeaderSlot = flyerData.pages[0]?.slots?.[0];
    const submitHasHeader =
      submitHeaderSlot?.type === 'promo' &&
      (submitHeaderSlot.promoSize === 'header_2x1' || submitHeaderSlot.promoSize === 'header_2x2') &&
      // Osiřelý slot bez obrázku (smazané promo hlavičky) hlavička není.
      !!submitHeaderSlot.promoImage;
    if (!submitHasHeader) {
      alert('Leták nemá hlavičku. Přidejte na první stránku hlavičku (promo velikosti „Hlavička 2×1" nebo „Hlavička 2×2").');
      return;
    }

    try {
      setIsValidating(true);
      // Kontrola i odeslání čtou leták z databáze, ne z editoru. Bez uložení by
      // se kontrolovala poslední uložená verze - typicky kopie s právě vybranou,
      // ale ještě neuloženou akcí propadla s „EAN nebyl nalezen" u všech produktů.
      await saveDraftMutation.mutateAsync(flyerData);
      const validationResult = await flyersService.validateFlyer(id);
      setIsValidating(false);

      if (!validationResult.valid) {
        // Show validation errors modal
        setValidationErrors(validationResult.errors);
        setShowValidationModal(true);
        return; // Do not proceed with submission
      }

      // All validations passed, proceed with submission
      await submitMutation.mutateAsync();
    } catch (error: any) {
      setIsValidating(false);
      console.error('Chyba při validaci letáku:', error);
      // Ukázat konkrétní hlášku z backendu (např. chybějící patička/hlavička), když je k dispozici.
      alert(error?.response?.data?.message || 'Leták se nepodařilo uložit a zkontrolovat. Zkuste to znovu.');
    }
  };

  const createCopyMutation = useMutation({
    mutationFn: async () => {
      if (!flyer) throw new Error('No flyer data');

      // Create new flyer with copied data
      const newFlyer = await flyersService.createFlyer({
        name: `${flyer.name} (kopie)`,
        // Akce se do kopie záměrně nepřebírá - uživatel ji vybere znovu.
        validFrom: flyer.validFrom || '',
        validTo: flyer.validTo || '',
      });

      // Copy pages structure
      if (flyer.pages && flyer.pages.length > 0) {
        await flyersService.updateFlyer(newFlyer.id, {
          pages: preparePagesForAPI(flyer.pages),
        });
      }

      return newFlyer;
    },
    onSuccess: (newFlyer) => {
      // Navigate to the new flyer
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      // Prodejce skládá letáky jen z produktů aktuálních letáků, popisy se mu
      // tedy nemají jak rozejít - hlášku dostávají jen ostatní role.
      if (!jeProdejce) nastavKontroluKopie(newFlyer.id, true);
      navigate(`${basePath}/${newFlyer.id}`);
    },
    onError: (error) => {
      console.error('Error creating flyer copy:', error);
      alert('Chyba při vytváření kopie letáku');
    },
  });

  const handleCreateCopy = () => {
    createCopyMutation.mutate();
  };

  const expireMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No flyer ID');
      return flyersService.expireFlyer(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      queryClient.invalidateQueries({ queryKey: ['flyers', id] });
      alert('Leták byl ukončen a produkty jsou nyní volné k editaci');
    },
    onError: (error: any) => {
      console.error('Error expiring flyer:', error);
      alert('Chyba při ukončování letáku: ' + (error.response?.data?.message || error.message));
    },
  });

  const handleExpireFlyer = async () => {
    if (!window.confirm('Opravdu chcete ukončit platnost tohoto letáku? Datum platnosti bude nastaveno na včerejší den a produkty budou uvolněny k editaci.')) {
      return;
    }
    await expireMutation.mutateAsync();
  };

  // Prodejce: obnovit vlastní archivovaný leták zpět do konceptu (i z editoru).
  const unarchiveMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No flyer ID');
      return flyersService.unarchiveFlyer(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      queryClient.invalidateQueries({ queryKey: ['flyers', id] });
      alert('Leták byl obnoven do konceptu a je opět editovatelný.');
    },
    onError: (error: any) => {
      alert('Chyba při obnovování letáku: ' + (error.response?.data?.message || error.message));
    },
  });

  // Get all product IDs that are already used in the flyer
  const usedProductIds = new Set(
    flyerData.pages.flatMap(page =>
      page.slots
        .filter(slot => slot?.type === 'product' && slot.product)
        .map(slot => slot.product!.id)
    )
  );

  // Krajní meze posuvníku se odvozují z aktuálně nabízených produktů.
  useEffect(() => {
    if (!isMyFlyers || allProducts.length === 0) return;
    const prices = allProducts.map((p) => Number(p.price));
    setMinPrice(Math.floor(Math.min(...prices)));
    setMaxPrice(Math.ceil(Math.max(...prices)));
  }, [allProducts, isMyFlyers]);

  // priceRange zůstává null, dokud uživatel nehne posuvníkem. Kdyby se rozsah
  // přednastavil na [min, max], po změně seznamu produktů (jiné aktivní letáky)
  // by zastaralé krajní hodnoty tiše odfiltrovaly nově dostupné produkty.
  const effectivePriceRange: [number, number] = priceRange ?? [minPrice, maxPrice];
  const isPriceFiltered = priceRange !== null;
  const hasActiveFilters =
    !!filterEan || !!filterBrandId || !!filterCategoryId || !!filterSubcategoryId ||
    !!filterInstallationType || isPriceFiltered;

  const clearFilters = () => {
    setFilterEan('');
    setFilterBrandId('');
    setFilterCategoryId('');
    setFilterSubcategoryId('');
    setFilterInstallationType('');
    setPriceRange(null);
  };

  // Dodavatel filtruje přes stránkované hledání na serveru, prodejce má
  // celý seznam v paměti - rozšířené filtry se proto uplatňují jen u něj.
  const filteredProducts = isMyFlyers
    ? allProducts.filter((product) => {
        if (priceRange) {
          const price = Number(product.price);
          if (price < priceRange[0] || price > priceRange[1]) return false;
        }
        if (filterEan && !product.eanCode?.includes(filterEan)) return false;
        if (filterBrandId && product.brandId !== filterBrandId) return false;
        if (filterCategoryId && product.categoryId !== filterCategoryId) return false;
        // BEZ_PODKATEGORIE = jen produkty, kterým podkategorie nebyla přiřazená (běžný druh,
        // viz docs/navrh-Umisteni-a-Podkategorie.md). Prázdný filtr ukazuje vše včetně nich.
        if (filterSubcategoryId === BEZ_PODKATEGORIE) {
          if (product.subcategoryId) return false;
        } else if (filterSubcategoryId && product.subcategoryId !== filterSubcategoryId) return false;
        if (filterInstallationType && product.installationType !== filterInstallationType) return false;
        return true;
      })
    : allProducts;

  const filteredPromoImages = promoImages.filter(p =>
    p.name.toLowerCase().includes(promoSearch.toLowerCase()) &&
    sedneNaFiltr(p.defaultSize, promoFiltr)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Rejection History - Full Width Top */}
        <RejectionHistory approvals={flyer?.approvals} rejectionReason={flyer?.rejectionReason} />

        {/* Locked Flyer Warning */}
        {isLocked && (
          <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Leták je ve stavu "{
                    flyer?.status === 'active' ? 'aktivní' :
                    flyer?.status === 'expired' ? 'vypršelý' :
                    'ke schválení'
                  }".</strong> Editace letáku je zakázána.
                  {/* Jen u vypršelého letáku: produkty v něm už nejsou zamčené, takže se
                      jejich cena mohla po vypršení změnit. Uložené PDF je snímek a zůstává
                      správně, tenhle náhled se ale kreslí z živých dat. U aktivního letáku
                      a letáku ke schválení jsou produkty zamčené, tam by hláška mátla. */}
                  {flyer?.status === 'expired' && ' Ceny nemusí souhlasit.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - 2 column layout */}
        <div className="grid grid-cols-5 gap-6 mb-6">
          {/* Left Column: Controls & Products/Promos */}
          <div className="col-span-2 flex flex-col gap-6">
            {/* Header Panel */}
            <div className="bg-white rounded-lg shadow p-4 flex-shrink-0">
              <div className="mb-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Add highlight parameter to scroll back to this flyer
                    const flyerId = params.id;
                    if (flyerId && flyerId !== 'new') {
                      navigate(`${basePath}?highlight=${flyerId}`);
                    } else {
                      navigate(basePath);
                    }
                  }}
                  size="sm"
                  className="mb-3"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět
                </Button>
                <Input
                  value={flyerData.name}
                  maxLength={150}
                  onChange={(e) => setFlyerData({ ...flyerData, name: e.target.value })}
                  className="text-lg font-bold border-0 border-b-2 rounded-none focus:ring-0 mb-2"
                  placeholder="Název letáku"
                  disabled={isLocked}
                />
                <span className="text-xs text-gray-500">
                  {isSaving ? 'Ukládání...' : lastSaved ? `Uloženo ${lastSaved.toLocaleTimeString()}` : ''}
                </span>
              </div>

              {user?.role !== 'end_user' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Název akce</label>
                  <select
                    value={flyerData.actionId ?? ''}
                    onChange={(e) => {
                      const actionId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                      const action = actions.find((a) => a.id === actionId);
                      setFlyerData({
                        ...flyerData,
                        actionId,
                        actionName: action?.name,
                        // Platnost je daná akcí (server ji stejně bere z ERP) - vždy přepsat.
                        validFrom: action?.validFrom || '',
                        validTo: action?.validTo || '',
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isLocked}
                  >
                    <option value="">Vyberte akci</option>
                    {actions.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-4">
                <Input
                  type="date"
                  label="Platnost od"
                  value={flyerData.validFrom}
                  onChange={(e) => setFlyerData({ ...flyerData, validFrom: e.target.value })}
                  disabled={isLocked || jeProdejce || jeDodavatel}
                  title={
                    jeProdejce
                      ? 'Den založení letáku, nastavuje se automaticky'
                      : jeDodavatel
                      ? 'Daná prodejní akcí, mění se jen výběrem akce'
                      : undefined
                  }
                />
                <Input
                  type="date"
                  label="Platnost do"
                  value={flyerData.validTo}
                  onChange={(e) => setFlyerData({ ...flyerData, validTo: e.target.value })}
                  disabled={isLocked || jeDodavatel}
                  title={jeDodavatel ? 'Daná prodejní akcí, mění se jen výběrem akce' : undefined}
                />
              </div>

              <div className="flex flex-col space-y-2">
                {!jeUzavrenyArchiv && (
                  <>
                    <Button variant="outline" onClick={handleViewPdf} isLoading={isGeneratingPdf} disabled={isNew || (flyer?.status !== 'draft' && !flyer?.hasPdf)} size="sm">
                      <FileText className="w-4 h-4 mr-2" />
                      {flyer?.status === 'draft' ? 'Ulož a Generuj PDF' : 'Zobrazit PDF'}
                    </Button>
                    <Button variant="outline" onClick={handleDownloadPdf} isLoading={isGeneratingPdf} disabled={isNew || (flyer?.status !== 'draft' && !flyer?.hasPdf)} size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      Stáhnout PDF
                    </Button>
                  </>
                )}
                {!isNew && !jeVlastniArchiv && (
                  <Button
                    variant="outline"
                    onClick={handleCreateCopy}
                    isLoading={createCopyMutation.isPending}
                    disabled={createCopyMutation.isPending}
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Vytvořit kopii
                  </Button>
                )}
                {flyer?.status === 'active' && (
                  user?.role === 'admin' ||
                  user?.role === 'approver' ||
                  (user?.role === 'end_user' && flyer?.supplierId === user?.id)
                ) && (
                  <Button
                    variant="outline"
                    onClick={handleExpireFlyer}
                    isLoading={expireMutation.isPending}
                    size="sm"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Ukončit platnost
                  </Button>
                )}
                {/* Prodejce: obnovit vlastní archivovaný leták zpět do konceptu (i z editoru).
                    Po uplynutí platnosti, tedy po archivaci vždy, se nenabízí. */}
                {isMyFlyers && jeVlastniArchiv && !jeUzavrenyArchiv && (
                  <Button
                    variant="outline"
                    onClick={() => unarchiveMutation.mutate()}
                    isLoading={unarchiveMutation.isPending}
                    disabled={unarchiveMutation.isPending}
                    size="sm"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Obnovit
                  </Button>
                )}
                <Button variant="outline" onClick={() => saveDraftMutation.mutate(flyerData)} isLoading={saveDraftMutation.isPending} disabled={isLocked} size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  Uložit
                </Button>
                {/* Hide submit button for end users on /my-flyers */}
                {!(isMyFlyers && user?.role === 'end_user') && (
                  <Button onClick={handleSubmit} isLoading={submitMutation.isPending || isValidating} disabled={isLocked} size="sm">
                    <Send className="w-4 h-4 mr-2" />
                    {isValidating ? 'Validuji...' : 'Odeslat k autorizaci'}
                  </Button>
                )}
              </div>
            </div>

            {/* Products & Promos with Tabs */}
            <div className="bg-white rounded-lg shadow flex flex-col overflow-hidden max-h-[680px]">
              {/* Tabs */}
              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab('products')}
                  className={`flex-1 px-4 py-3 text-sm font-medium ${
                    activeTab === 'products'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Search className="w-4 h-4 inline mr-2" />
                  Produkty
                </button>
                <button
                  onClick={() => setActiveTab('promos')}
                  className={`flex-1 px-4 py-3 text-sm font-medium ${
                    activeTab === 'promos'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Promo obrázky
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-4 flex-1 flex flex-col overflow-hidden">
                {activeTab === 'products' ? (
                  <>
                    <Input
                      placeholder="Hledat produkty..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="mb-4 flex-shrink-0"
                    />

                    {/* Rozšířené filtry - jen pro prodejce */}
                    {isMyFlyers && (
                      <div className="mb-4 border border-gray-200 rounded-lg flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                          className="w-full px-3 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-t-lg"
                        >
                          <span>
                            Rozšířené filtry
                            {hasActiveFilters && (
                              <span className="ml-2 text-xs font-normal text-blue-600">(aktivní)</span>
                            )}
                          </span>
                          {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        {showAdvancedFilters && (
                          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                            {/* Cenové rozpětí */}
                            {maxPrice > minPrice && (
                              <div className="mb-3">
                                <div className="flex justify-between items-center mb-1">
                                  <label className="text-xs font-medium text-gray-700">Cenové rozpětí</label>
                                  <span className="text-xs text-gray-600">
                                    {effectivePriceRange[0].toLocaleString('cs-CZ')} - {effectivePriceRange[1].toLocaleString('cs-CZ')} Kč
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="range"
                                    min={minPrice}
                                    max={maxPrice}
                                    value={effectivePriceRange[0]}
                                    onChange={(e) => {
                                      const value = Math.min(Number(e.target.value), effectivePriceRange[1]);
                                      setPriceRange([value, effectivePriceRange[1]]);
                                    }}
                                    className="w-full h-1.5 accent-blue-600 cursor-pointer"
                                    aria-label="Minimální cena"
                                  />
                                  <input
                                    type="range"
                                    min={minPrice}
                                    max={maxPrice}
                                    value={effectivePriceRange[1]}
                                    onChange={(e) => {
                                      const value = Math.max(Number(e.target.value), effectivePriceRange[0]);
                                      setPriceRange([effectivePriceRange[0], value]);
                                    }}
                                    className="w-full h-1.5 accent-blue-600 cursor-pointer"
                                    aria-label="Maximální cena"
                                  />
                                </div>
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2 mb-2">
                              {/* EAN */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">EAN kód</label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={filterEan}
                                    onChange={(e) => setFilterEan(e.target.value)}
                                    placeholder="Hledat..."
                                    className="w-full px-2 py-1 pr-6 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                  {filterEan && (
                                    <button
                                      type="button"
                                      onClick={() => setFilterEan('')}
                                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      aria-label="Vymazat EAN"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Značka */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Značka</label>
                                <select
                                  value={filterBrandId}
                                  onChange={(e) => setFilterBrandId(e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="">Všechny</option>
                                  {filterBrands.map((brand) => (
                                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Kategorie */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Kategorie</label>
                                <select
                                  value={filterCategoryId}
                                  onChange={(e) => {
                                    setFilterCategoryId(e.target.value);
                                    // Podkategorie i typ spotřebiče patří ke konkrétní kategorii.
                                    setFilterSubcategoryId('');
                                    setFilterInstallationType('');
                                  }}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="">Všechny</option>
                                  {filterCategories.map((category: any) => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Podkategorie - jen když má vybraná kategorie nějaké */}
                              {filterCategoryId && filterSubcategories.length > 0 && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Podkategorie</label>
                                  <select
                                    value={filterSubcategoryId}
                                    onChange={(e) => setFilterSubcategoryId(e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="">Všechny</option>
                                    <option value={BEZ_PODKATEGORIE}>Bez podkategorie</option>
                                    {filterSubcategories.map((subcategory: any) => (
                                      <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              {/* Typ spotřebiče - jen u kategorií, které to rozlišují */}
                              {filterCategoryId &&
                                filterCategories.find((c: any) => c.id === filterCategoryId)?.requiresInstallationType && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Typ</label>
                                  <select
                                    value={filterInstallationType}
                                    onChange={(e) => setFilterInstallationType(e.target.value as '' | 'BUILT_IN' | 'FREESTANDING')}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="">Všechny</option>
                                    <option value="BUILT_IN">Vestavné</option>
                                    <option value="FREESTANDING">Volně stojící</option>
                                  </select>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between pt-1.5 border-t border-gray-200">
                              <div className="text-xs text-gray-500">
                                <strong>{filteredProducts.length}</strong> z <strong>{allProducts.length}</strong> produktů
                              </div>
                              {hasActiveFilters && (
                                <button
                                  type="button"
                                  onClick={clearFilters}
                                  className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-300 rounded"
                                >
                                  Vymazat filtry
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-2 flex-1 overflow-y-auto">
                      {(!isMyFlyers && isLoadingProducts && productPage === 1) ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : filteredProducts.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                          {isMyFlyers ? 'Žádné produkty v aktivních letácích' : 'Žádné produkty'}
                        </p>
                      ) : (
                        <>
                          {filteredProducts.map(product => (
                            <DraggableProduct
                              key={product.id}
                              product={product}
                              isUsed={usedProductIds.has(product.id)}
                              disabled={isLocked}
                            />
                          ))}
                          {!isMyFlyers && productsData && productsData.meta.total > allProducts.length && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setProductPage(prev => prev + 1)}
                              isLoading={isLoadingProducts}
                              className="w-full mt-2"
                            >
                              Načíst více ({allProducts.length} z {productsData.meta.total})
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Hledat promo obrázky..."
                      value={promoSearch}
                      onChange={(e) => setPromoSearch(e.target.value)}
                      className="mb-2 flex-shrink-0"
                    />
                    {/* Nízký filtrační řádek - panel je úzký, tak jen drobné přepínače. */}
                    <div className="flex flex-wrap justify-end gap-1 mb-3 flex-shrink-0">
                      {PROMO_FILTRY.map(f => {
                        const aktivni = promoFiltr === f.key;
                        return (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => setPromoFiltr(f.key)}
                            aria-pressed={aktivni}
                            title={f.key === 'vse' ? 'Bez filtru' : `Zobrazit jen: ${f.label}`}
                            className={`px-2 py-0.5 rounded-full border text-xs font-medium transition-colors ${
                              aktivni
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="space-y-2 flex-1 overflow-y-auto">
                      {filteredPromoImages.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                          {promoImages.length === 0 ? (
                            <>Žádné promo obrázky.<br />Nahrajte je v sekci "Promo obrázky"</>
                          ) : (
                            'Nenalezeny žádné výsledky'
                          )}
                        </p>
                      ) : (
                        filteredPromoImages.map(promo => <DraggablePromoImage key={promo.id} promoImage={promo} disabled={isLocked} />)
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Flyer Preview */}
          <div className="col-span-3">
            {/* Flyer Preview */}
            <FlyerPageView
              page={flyerData.pages[currentPageIndex]}
              pageIndex={currentPageIndex}
              onRemoveProduct={handleRemoveProduct}
              onRemoveFooter={handleRemoveFooter}
              isEditable={!isLocked}
              validFrom={flyerData.validFrom}
              validTo={flyerData.validTo}
            />
          </div>
        </div>

        {/* Page Navigation - Full Width Bottom */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center gap-4">
            <h3 className="font-bold flex-shrink-0">Stránka {currentPageIndex + 1} / {flyerData.pages.length}</h3>
            <div className="flex items-center gap-4 min-w-0 flex-1 justify-end">
              <SortableContext
                items={flyerData.pages.map((_, index) => `page-button-${index}`)}
                strategy={rectSortingStrategy}
              >
                {/* Cudliky stranek se zalamuji - u letaku s vice nez ~24 strankami
                    by jedna rada pretekla ven z karty. */}
                <div className="flex flex-wrap justify-end gap-2 min-w-0">
                  {flyerData.pages.map((_, index) => (
                    <SortablePageButton
                      key={`page-button-${index}`}
                      id={`page-button-${index}`}
                      index={index}
                      isActive={currentPageIndex === index}
                      isFirst={index === 0}
                      isLocked={isLocked}
                      onClick={() => setCurrentPageIndex(index)}
                    />
                  ))}
                </div>
              </SortableContext>
              <div className="flex space-x-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemovePage(currentPageIndex)}
                  disabled={flyerData.pages.length === 1 || flyerData.pages[currentPageIndex]?.pageNumber === 1 || isLocked}
                >
                  <Minus className="w-4 h-4 mr-1" />
                  Odebrat
                </Button>
                <Button variant="outline" size="sm" onClick={handleAddPage} disabled={isLocked}>
                  <Plus className="w-4 h-4 mr-1" />
                  Přidat
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeProduct && <DraggableProduct product={activeProduct} />}
      </DragOverlay>

      {/* Validation Errors Modal */}
      <ValidationErrorsModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        errors={validationErrors}
        flyerName={flyerData.name}
      />

      {/* Povinná hláška po vytvoření kopie - zavře se jen tlačítkem,
          ne kliknutím vedle ani klávesou Esc. */}
      {kopieKontrola && !isNew && !jeProdejce && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div
            role="alertdialog"
            aria-modal="true"
            className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6"
          >
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0" />
              <p className="text-gray-900">
                Mohlo dojít ke změně popisů produktů v letáku. Zkontrolujte.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={potvrditKontroluKopie} autoFocus>
                Rozumím
              </Button>
            </div>
          </div>
        </div>
      )}
    </DndContext>
  );
};
